import RedeemIcon from '@mui/icons-material/Redeem';
import { Role } from '@okletsplay/core';
import { useKeycloak } from '@react-keycloak/web';
import { ModalName, Route } from 'app/enums';
import { NavigationMenuItem } from 'app/interfaces/sidebar';
import { modalsReducers } from 'app/store/modal/slice';
import { useCallback, useMemo, useState } from 'react';
import config from 'config/config';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useRoles } from 'modules/auth/hooks';
import { AffiliateProgramConfig } from '../../../config/affiliate-program-config';

interface RightSideMenuHook {
  myProfileMenu: NavigationMenuItem[];
  authorizedActionsMenu: NavigationMenuItem[];
  nonAuthorizedActionsMenu: NavigationMenuItem[];
  linksMenu: NavigationMenuItem[];

  isLogoutOpen: boolean;
  onLogoutHandler: () => void;
  onLoginHandler: () => void;
}

export function useRightSideMenu(): RightSideMenuHook {
  // TODO: update to normal modal windows AND remove from the hook
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const { keycloak } = useKeycloak();
  const { hasAllRoles } = useRoles();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onLogoutHandler = useCallback(() => {
    setIsLogoutOpen((prevState) => !prevState);
  }, []);

  const onLoginHandler = useCallback(() => {
    window.history.pushState(window.history.state, document.title, document.location.href);
    keycloak?.login();
  }, [keycloak]);

  const myProfileMenu: NavigationMenuItem[] = useMemo(() => {
    const menuItems: NavigationMenuItem[] = [
      {
        label: 'My Profile',
        onClick: () => navigate(Route.MyProfile),
      },
      {
        label: 'Manage Balance',
        onClick: () => navigate(Route.PaymentBalance),
        isAllowedWithoutAuthorization: true,
      },
      {
        label: 'Transaction History',
        onClick: () => navigate(Route.PaymentTransactions),
      },
      {
        label: 'Platform Linking',
        onClick: () => navigate(Route.PlatformLinking),
        isAllowedWithoutAuthorization: true,
      },
      {
        label: 'Give Feedback',
        onClick: () => dispatch(modalsReducers.openModal({ name: ModalName.Feedback })),
      },
    ];

    if (AffiliateProgramConfig.isAffiliateProgramOwnerViewEnabled()) {
      const affiliateProgramMenuItem: NavigationMenuItem = {
        icon: RedeemIcon,
        label: 'Affiliate Programs',
        roles: [Role.AffiliateProgramOwner],
        onClick: () => navigate(Route.AffiliatePrograms),
      };

      menuItems.splice(2, 0, affiliateProgramMenuItem);
    }

    return menuItems.filter((item) => hasAllRoles(item?.roles ?? []));
  }, [navigate, dispatch, hasAllRoles]);

  const authorizedActionsMenu: NavigationMenuItem[] = useMemo(() => {
    if (!keycloak?.authenticated) {
      return [];
    }

    return [
      {
        label: 'Log Out',
        onClick: onLogoutHandler,
        dataTestId: 'log-out-menu-item',
      },
    ];
  }, [keycloak?.authenticated, onLogoutHandler]);

  const nonAuthorizedActionsMenu: NavigationMenuItem[] = useMemo(() => {
    if (keycloak?.authenticated) {
      return [];
    }

    return [
      {
        label: 'Log In / Sign Up',
        onClick: onLoginHandler,
        isPrimary: true,
        dataTestId: 'login-register-menu-item',
      },
    ];
  }, [keycloak?.authenticated, onLoginHandler]);

  const linksMenu: NavigationMenuItem[] = useMemo(
    () => [
      {
        label: 'Legal',
        onClick: () => window.open(config?.termsOfUseUrl, '_blank'),
        hideOnClick: false,
      },
    ],
    []
  );

  return {
    myProfileMenu,
    authorizedActionsMenu,
    nonAuthorizedActionsMenu,
    linksMenu,
    isLogoutOpen,
    onLoginHandler,
    onLogoutHandler,
  };
}
